import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { API } from 'aws-amplify';
import { ConfigService } from '../../config';
import { VoucherDemoModeService } from '../../vouchers/demo/voucher-demo-mode.service';
import { buildDemoSalesSummary } from '../../vouchers/demo/voucher-demo-dashboard';

export type HahaExploreResource =
	| 'markets'
	| 'market_detail'
	| 'market_online_status'
	| 'market_planogram'
	| 'market_restock'
	| 'sales'
	| 'sale_detail';

export interface HahaExploreQuery {
	page?: string;
	page_size?: string;
	sort?: string;
	cursor?: string;
	sticker_num?: string;
	start_time?: string;
	end_time?: string;
	status?: string;
}

export interface HahaExplorePayload {
	resource: HahaExploreResource;
	marketId?: string;
	orderNo?: string;
	query?: HahaExploreQuery;
}

export interface HahaExploreResult {
	resource: HahaExploreResource;
	method: 'GET';
	path: string;
	query: HahaExploreQuery;
	haha_status: number;
	haha_body: unknown;
}

export interface HahaDeviceAssignment {
	disti_id: number | null;
	market_id: number | null;
	client_id: number | null;
	campus_id: number | null;
	campus_name?: string;
	assigned_at: string;
}

// The vending machine a cold brew fridge sits beside. Broken when the machine is gone or now
// on a different campus than the fridge.
export interface HahaDevicePairing {
	machine_id: string;
	machine_name: string;
	machine_campus_id: number | null;
	broken: boolean;
}

export interface HahaDevice {
	haha_market_id: string;
	haha_market_name: string;
	time_zone: string;
	location?: string;
	device_type?: string;
	status: string;
	is_online: boolean;
	assignment: HahaDeviceAssignment | null;
	pairing?: HahaDevicePairing | null;
	// Today and the last 7 Pacific days, as on the fridge's page; null when sales couldn't be read
	sales?: { today: HahaFleetSalesPeriod; last_7_days: HahaFleetSalesPeriod } | null;
}

export interface HahaFleetSalesPeriod {
	orders: number;
	units: number;
	revenue_cents: number;
}

export interface HahaDeviceInfo {
	haha_market_id: string;
	name: string;
	location: string;
	time_zone: string;
	device_type: string;
	status: string;
	is_online: boolean | null;
	doors: number | null;
	shelves: number | null;
	activated_at: string | null;
}

export interface HahaDeviceStockProduct {
	product_id: string;
	product_name: string;
	stock: number;
	capacity: number;
	// From Haha's catalogue; empty when it couldn't be loaded
	price: string;
	cost: string;
	price_unit: string;
	image_url: string;
	// Average over the last 14 complete days, and how long stock lasts at that pace. days_left is
	// null when the product didn't sell; both are missing when sales couldn't be read.
	daily_units?: number;
	days_left?: number | null;
}

export type HahaSlotState = 'empty' | 'low' | 'ok';

export interface HahaStockSlot {
	column: number;
	product_id: string;
	product_name: string;
	stock: number;
	// null when the slot has no standard capacity in Haha
	capacity: number | null;
	state: HahaSlotState;
	image_url: string;
}

export interface HahaStockDoor {
	name: string;
	label: string;
	shelves: Array<{ shelf: number; slots: HahaStockSlot[] }>;
}

export interface HahaDeviceStock {
	total: number;
	// null when Haha has no standard capacity for the slots
	capacity: number | null;
	changed_at: string | null;
	empty_slots: number;
	low_slots: number;
	doors: HahaStockDoor[];
	products: HahaDeviceStockProduct[];
	// The days the sales pace covers
	sales_pace?: { start: string; end: string; days: number };
}

export interface HahaSalesPeriod {
	orders: number;
	refunds: number;
	units: number;
	revenue_cents: number;
}

export interface HahaDeviceSales {
	haha_market_id: string;
	range: { start: string; end: string; time_zone: string };
	currency_code: string;
	periods: { today: HahaSalesPeriod; last_7_days: HahaSalesPeriod; last_30_days: HahaSalesPeriod };
	// One entry per day in the range, oldest first
	daily: Array<HahaSalesPeriod & { date: string }>;
	top_products: Array<{ product_id: string; product_name: string; units: number; revenue_cents: number }>;
	recent_orders: Array<{
		sale_id: string;
		sold_at: string | null;
		total_cents: number;
		is_refund: boolean;
		payment_type: string;
		items: Array<{ product_id: string; product_name: string; quantity: number }>;
	}>;
	// Every door open over the "last 7 days" period, and how many took nothing (NOT_CONSUMED)
	not_consumed_7_days: { door_opens: number; not_consumed: number };
	// When vendibean-jobs last copied orders from Haha (every 15 minutes), UTC ISO; null if never
	synced_at: string | null;
}

export interface HahaStatusSegment {
	status: 'online' | 'offline' | 'unknown';
	start: string;
	end: string;
	minutes: number;
}

// From vendibean-jobs' 5-minute status poll. "unknown" covers time before the fridge was first
// seen and time since the poll last ran.
export interface HahaDeviceStatusHistory {
	haha_market_id: string;
	range: { start: string; end: string };
	polled_at: string | null;
	// Oldest first, covering the whole range
	segments: HahaStatusSegment[];
	summary: {
		online_minutes: number;
		offline_minutes: number;
		unknown_minutes: number;
		// Of the time we have data for; null when there's none
		online_percent: number | null;
		times_went_offline: number;
	};
}

export interface HahaStockChange {
	changed_at: string | null;
	operation_type: number | null;
	label: string;
	operator_name: string;
	method: string;
	door: number | null;
	// null when Haha has no product detail for the change
	products: Array<{ product_id: string; product_name: string; change: number; after: number | null; image_url: string }> | null;
}

export interface HahaDeviceStockChanges {
	haha_market_id: string;
	fetched_at: string;
	changes: HahaStockChange[];
}

export interface HahaDeviceDetail {
	device: HahaDeviceInfo;
	assignment: HahaDeviceAssignment | null;
	pairing: HahaDevicePairing | null;
	pairing_candidates: Array<{ machine_id: string; machine_name: string }>;
	stock: HahaDeviceStock | null;
	// Haha sections that could not be loaded: 'stock'
	unavailable: string[];
}

export interface MachinePairedHahaDevice {
	machine_id: string;
	device: HahaDeviceInfo | null;
	assignment: HahaDeviceAssignment | null;
	pairing: HahaDevicePairing | null;
}

export interface ListHahaDevicesResult {
	devices: HahaDevice[];
	haha_total: number;
	sales_range?: { start: string; end: string } | null;
	// When vendibean-jobs last copied orders from Haha
	sales_synced_at?: string | null;
}

export interface AssignHahaDevicePayload {
	disti_id: number;
	market_id: number;
	client_id: number;
	campus_id: number;
}

export interface AssignHahaDeviceResult {
	haha_market_id: string;
	assignment: HahaDeviceAssignment;
	// true when the campus changed and the fridge's pairing ended
	pairing_cleared?: boolean;
}

export interface HahaSalesDeviceSummary {
	haha_market_id: string;
	client_id: number | null;
	campus_id: number | null;
	txn_count: number;
	refund_txn_count: number;
	unit_count: number;
	revenue_cents: number;
}

export interface HahaSalesSummaryResult {
	scope: {
		client_id: number | null;
		campus_id: number | null;
	};
	start_time: string;
	end_time: string;
	haha: {
		device_count: number;
		txn_count: number;
		refund_txn_count: number;
		unit_count: number;
		revenue_cents: number;
		devices: HahaSalesDeviceSummary[];
	};
	vending: {
		status: 'stripe_readers';
		machine_count: number;
		mapped_reader_count: number;
		txn_count: number;
		refund_txn_count: number;
		revenue_cents: number | null;
	};
	purchased: {
		txn_count: number;
		revenue_cents: number;
	};
	daily_units?: Array<{
		date: string;
		vending_units: number;
		haha_units: number;
	}>;
	minimum_cents: number;
	shortfall_cents: number;
	quantity: number;
}

export interface HahaSalesSummaryQuery {
	client_id?: number | null;
	campus_id?: number | null;
	start_time: string;
	end_time: string;
}

export interface HahaApiResponse<T> {
	status: number;
	message: string;
	data: T;
	error?: unknown;
}

@Injectable({
	providedIn: 'root'
})
export class HahaHttpService {
	private apiName: string;

	constructor(
		private configService: ConfigService,
		private demoMode: VoucherDemoModeService
	) {
		this.apiName = this.configService.get('host');
	}

	explore(payload: HahaExplorePayload): Observable<HahaApiResponse<HahaExploreResult>> {
		return new Observable(observer => {
			const path = '/dashboard/haha/explore';
			const myInit = {
				response: false,
				body: payload
			};

			API.post(this.apiName, path, myInit)
				.then(response => {
					observer.next(response);
					observer.complete();
				})
				.catch(error => observer.error(error));
		});
	}

	listDevices(): Observable<HahaApiResponse<ListHahaDevicesResult>> {
		return new Observable(observer => {
			const path = '/dashboard/haha/devices';
			const myInit = {
				response: false
			};

			API.get(this.apiName, path, myInit)
				.then(response => {
					observer.next(response);
					observer.complete();
				})
				.catch(error => observer.error(error));
		});
	}

	getSalesSummary(
		query: HahaSalesSummaryQuery
	): Observable<HahaApiResponse<HahaSalesSummaryResult>> {
		if (this.demoMode.isEnabled()) {
			return of({
				status: 200,
				message: 'ok',
				data: buildDemoSalesSummary(query)
			});
		}
		return new Observable(observer => {
			const path = '/dashboard/haha/sales-summary';
			const queryStringParameters: Record<string, string> = {
				start_time: query.start_time,
				end_time: query.end_time
			};
			if (query.campus_id !== null && query.campus_id !== undefined) {
				queryStringParameters.campus_id = String(query.campus_id);
			} else if (query.client_id !== null && query.client_id !== undefined) {
				queryStringParameters.client_id = String(query.client_id);
			}

			const myInit = {
				response: false,
				queryStringParameters
			};

			API.get(this.apiName, path, myInit)
				.then(response => {
					observer.next(response);
					observer.complete();
				})
				.catch(error => observer.error(error));
		});
	}

	getDevice(hahaMarketId: string): Observable<HahaApiResponse<HahaDeviceDetail>> {
		return this.request(() => API.get(this.apiName, `/dashboard/haha/devices/${encodeURIComponent(hahaMarketId)}`, {
			response: false
		}));
	}

	getDeviceSales(hahaMarketId: string): Observable<HahaApiResponse<HahaDeviceSales>> {
		return this.request(() => API.get(this.apiName, `/dashboard/haha/devices/${encodeURIComponent(hahaMarketId)}/sales`, {
			response: false
		}));
	}

	getDeviceStatusHistory(hahaMarketId: string): Observable<HahaApiResponse<HahaDeviceStatusHistory>> {
		return this.request(() => API.get(this.apiName, `/dashboard/haha/devices/${encodeURIComponent(hahaMarketId)}/status-history`, {
			response: false
		}));
	}

	// Slow on a cold cache: Haha's restock list takes 5-15 seconds.
	getDeviceStockChanges(hahaMarketId: string): Observable<HahaApiResponse<HahaDeviceStockChanges>> {
		return this.request(() => API.get(this.apiName, `/dashboard/haha/devices/${encodeURIComponent(hahaMarketId)}/restocks`, {
			response: false
		}));
	}

	pairDevice(hahaMarketId: string, machineId: string): Observable<HahaApiResponse<unknown>> {
		return this.request(() => API.put(this.apiName, `/dashboard/haha/devices/${encodeURIComponent(hahaMarketId)}/pairing`, {
			response: false,
			body: { machine_id: machineId }
		}));
	}

	unpairDevice(hahaMarketId: string): Observable<HahaApiResponse<unknown>> {
		return this.request(() => API.del(this.apiName, `/dashboard/haha/devices/${encodeURIComponent(hahaMarketId)}/pairing`, {
			response: false
		}));
	}

	getMachinePairedDevice(machineId: string): Observable<HahaApiResponse<MachinePairedHahaDevice>> {
		return this.request(() => API.get(this.apiName, `/dashboard/haha/machines/${encodeURIComponent(machineId)}/paired-device`, {
			response: false
		}));
	}

	private request<T>(call: () => Promise<T>): Observable<T> {
		return new Observable(observer => {
			call()
				.then(response => {
					observer.next(response);
					observer.complete();
				})
				.catch(error => observer.error(error));
		});
	}

	assignDevice(
		hahaMarketId: string,
		payload: AssignHahaDevicePayload
	): Observable<HahaApiResponse<AssignHahaDeviceResult>> {
		return new Observable(observer => {
			const path = `/dashboard/haha/devices/${encodeURIComponent(hahaMarketId)}`;
			const myInit = {
				response: false,
				body: payload
			};

			API.put(this.apiName, path, myInit)
				.then(response => {
					observer.next(response);
					observer.complete();
				})
				.catch(error => observer.error(error));
		});
	}
}
